import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppProvider, useAppContext } from './context/AppContext';
import { canAccessRoute } from './utils/rbac';
import Layout from './components/layout/Layout';
import UploadModal from './components/common/UploadModal';
import VideoUploadModal from './components/common/VideoUploadModal';
import Loading from './components/common/Loading';
import Login from './pages/Login/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import ContentLibrary from './pages/ContentLibrary/ContentLibrary';
import ContentDetails from './pages/ContentDetails/ContentDetails';
import ExplainerVideos from './pages/ExplainerVideos/ExplainerVideos';
import ExplainerVideoDetails from './pages/ExplainerVideos/ExplainerVideoDetails';
import ModuleCreation from './pages/ModuleCreation/ModuleCreation';
import ModuleDetails from './pages/ModuleDetails/ModuleDetails';
import UserEnrollment from './pages/UserEnrollment/UserEnrollment';
import Profile from './pages/Profile/Profile';
import Settings from './pages/Settings/Settings';
import Analytics from './pages/Analytics/Analytics';
import TraineeAnalytics from './pages/TraineeAnalytics/TraineeAnalytics';
import TraineeLeaderboard from './pages/TraineeLeaderboard/TraineeLeaderboard';
import ModuleAnalytics from './pages/Analytics/ModuleAnalytics';
import TraineeDetails from './pages/TraineeDetails/TraineeDetails';
import Notifications from './pages/Notifications/Notifications';
import VirtualTours from './pages/VirtualTours/VirtualTours';
import VirtualTourDetails from './pages/VirtualTours/VirtualTourDetails';
import VRAccess from './pages/VRAccess/VRAccess';
import VRChannels from './pages/VRAccess/VRChannels';
import Help from './pages/Help/Help';
import NotFound from './pages/NotFound/NotFound';
import './styles/variables.css';
import './styles/global.css';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAppContext();
  const location = useLocation();

  if (loading) {
    return <Loading fullScreen={true} message="Checking authentication..." />;
  }

  if (!isAuthenticated) {
    // Store the intended route before redirecting to login
    sessionStorage.setItem('intendedRoute', location.pathname);
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return children;
};

const LoginRedirect = () => {
  const { isAuthenticated, loading } = useAppContext();
  const location = useLocation();

  if (loading) {
    return <Loading fullScreen={true} message="Checking authentication..." />;
  }

  // If already authenticated, redirect to intended route or dashboard
  if (isAuthenticated) {
    const intendedRoute = sessionStorage.getItem('intendedRoute') || location.state?.from || '/';
    sessionStorage.removeItem('intendedRoute');
    return <Navigate to={intendedRoute} replace />;
  }

  return <Login />;
};

const RoleProtectedRoute = ({ children, route }) => {
  const { isAuthenticated, currentUser, loading } = useAppContext();
  const location = useLocation();

  if (loading) {
    return <Loading fullScreen={true} message="Checking authentication..." />;
  }

  if (!isAuthenticated) {
    sessionStorage.setItem('intendedRoute', location.pathname);
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Check if user can access this route based on their role
  if (!canAccessRoute(currentUser, route)) {
    // Redirect to dashboard if user doesn't have permission
    return <Navigate to="/" replace />;
  }

  return children;
};

const MainApp = () => {
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/content-library" element={
          <RoleProtectedRoute route="/content-library">
            <ContentLibrary />
          </RoleProtectedRoute>
        } />
        <Route path="/content-library/:id" element={
          <RoleProtectedRoute route="/content-library/:id">
            <ContentDetails />
          </RoleProtectedRoute>
        } />
        <Route path="/explainer-videos" element={
          <RoleProtectedRoute route="/explainer-videos">
            <ExplainerVideos />
          </RoleProtectedRoute>
        } />
        <Route path="/explainer-videos/:id" element={
          <RoleProtectedRoute route="/explainer-videos/:id">
            <ExplainerVideoDetails />
          </RoleProtectedRoute>
        } />
        <Route path="/modules" element={
          <RoleProtectedRoute route="/modules">
            <ModuleCreation />
          </RoleProtectedRoute>
        } />
        <Route path="/modules/:id" element={
          <RoleProtectedRoute route="/modules/:id">
            <ModuleDetails />
          </RoleProtectedRoute>
        } />
        <Route path="/enrollment" element={
          <RoleProtectedRoute route="/enrollment">
            <UserEnrollment />
          </RoleProtectedRoute>
        } />
        <Route path="/profile" element={<Profile />} />
        <Route path="/settings" element={
          <RoleProtectedRoute route="/settings">
            <Settings />
          </RoleProtectedRoute>
        } />
        <Route path="/analytics" element={
          <RoleProtectedRoute route="/analytics">
            <Analytics />
          </RoleProtectedRoute>
        } />
        <Route path="/analytics/trainee/:username" element={
          <RoleProtectedRoute route="/analytics">
            <TraineeAnalytics />
          </RoleProtectedRoute>
        } />
        <Route path="/analytics/leaderboard" element={
          <RoleProtectedRoute route="/analytics">
            <TraineeLeaderboard />
          </RoleProtectedRoute>
        } />
        <Route path="/analytics/module/:id" element={
          <RoleProtectedRoute route="/analytics/module/:id">
            <ModuleAnalytics />
          </RoleProtectedRoute>
        } />
        <Route path="/trainees/:username" element={
          <RoleProtectedRoute route="/trainees/:username">
            <TraineeDetails />
          </RoleProtectedRoute>
        } />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/virtual-tours" element={
          <RoleProtectedRoute route="/virtual-tours">
            <VirtualTours />
          </RoleProtectedRoute>
        } />
        <Route path="/virtual-tours/:id" element={
          <RoleProtectedRoute route="/virtual-tours/:id">
            <VirtualTourDetails />
          </RoleProtectedRoute>
        } />
        <Route path="/vr-access" element={
          <RoleProtectedRoute route="/vr-access">
            <VRAccess />
          </RoleProtectedRoute>
        } />
        <Route path="/vr-access/channels" element={
          <RoleProtectedRoute route="/vr-access/channels">
            <VRChannels />
          </RoleProtectedRoute>
        } />
        <Route path="/help" element={<Help />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Layout>
  );
};

function App() {
  const { isAuthenticated } = useAppContext();

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<LoginRedirect />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <MainApp />
            </ProtectedRoute>
          }
        />
      </Routes>
      {isAuthenticated && <UploadModal />}
      {isAuthenticated && <VideoUploadModal />}
    </Router>
  );
}

const AppWrapper = () => {
  return (
    <AppProvider>
      <App />
    </AppProvider>
  );
};

export default AppWrapper;
